// @zvndev/yable-core — createTable() — Main Engine

import type {
  RowData,
  Table,
  TableOptions,
  TableOptionsResolved,
  TableState,
  ColumnDef,
  ColumnDefExtensions,
  Column,
  Row,
  RowModel,
  HeaderGroup,
  Header,
  Updater,
  ExportOptions,
  SortingState,
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  CellRange,
  VisibilityState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  ColumnSizingInfoState,
  ExpandedState,
  RowPinningState,
  GroupingState,
  EditingState,
  KeyboardNavigationAction,
  KeyboardNavigationCell,
  KeyboardNavigationState,
  YableEventMap,
} from '../types'
import { functionalUpdate, memo, makeStateUpdater } from '../utils'
import { createColumn } from './column'
import { createRow } from './row'
import { fireColumnCommitHooks } from './editCommitHooks'
import { buildHeaderGroups } from './headers'
import { EventEmitterImpl } from '../events/EventEmitter'
import { getDefaultLocale } from '../i18n/locales'
import type { YableLocale } from '../i18n/en'
import {
  getCellPositionByIds,
  getNextFocusedCell,
  normalizeFocusedCell,
} from '../features/keyboardNavigation'
import { applyCellPaste, parseClipboardText, serializeCells } from '../features/clipboard'
import {
  clampCellToTable,
  createCellRange,
  isCellInRange,
  normalizeCellRange,
} from '../features/selection'
import { createCommitCoordinator } from '../features/commits/coordinator'
import type { CommitCoordinator, CoordinatorOptions } from '../features/commits/coordinator'
import type { CellPatch, CommitsSlice } from '../features/commits/types'
import { createFullRowEditingIntegration } from '../features/fullRowEditing'
import { createRowDragIntegration } from '../features/rowDragging'
import { createUndoRedoIntegration } from '../features/undoRedo'
import { detectAndFill } from '../features/fillHandle'
import { getPivotRowModel } from '../features/pivot'
import type { PivotConfig as PivotEngineConfig } from '../features/pivot'
import { getTreeRowModel, type TreeNode } from '../features/treeData'
import { getGroupedRowModel as buildGroupedRowModel } from '../features/grouping'
import { FormulaEngine } from '../features/formulas/engine'

// ---------------------------------------------------------------------------
// Private commit coordinator storage — avoids `(table as any).__commitCoordinator`
// ---------------------------------------------------------------------------

const commitCoordinatorMap = new WeakMap<Table<RowData>, CommitCoordinator>()

// Re-export resolvers for convenience (they live in resolvers.ts to avoid circular deps)
export { resolveSortingFn, resolveFilterFn } from './resolvers'

/** Package-internal access to the commit coordinator for a given table instance. */
export function getCommitCoordinator(table: Table<RowData>): CommitCoordinator | undefined {
  return commitCoordinatorMap.get(table)
}

interface ColumnSizingItem {
  id: string
  min: number
  max: number | undefined
  weight: number
}

function getColumnSizingBounds<TData extends RowData>(
  column: Column<TData, unknown>,
): { min: number; max: number | undefined } {
  const def = column.columnDef as Partial<ColumnDefExtensions<TData, unknown>>
  const min = typeof def.minSize === 'number' && Number.isFinite(def.minSize) ? def.minSize : 0
  const max =
    typeof def.maxSize === 'number' && Number.isFinite(def.maxSize) ? def.maxSize : undefined
  const normalizedMin = Math.max(0, min)

  return {
    min: normalizedMin,
    max: typeof max === 'number' && max >= normalizedMin ? max : undefined,
  }
}

function getColumnFlex<TData extends RowData>(column: Column<TData, unknown>): number {
  const def = column.columnDef as Partial<ColumnDefExtensions<TData, unknown>>
  const flex = def.flex
  return typeof flex === 'number' && Number.isFinite(flex) && flex > 0 ? flex : 0
}

function distributeColumnSizes(
  items: ColumnSizingItem[],
  targetWidth: number,
): Map<string, number> {
  const sizes = new Map<string, number>()
  let active = [...items]
  let remaining = Math.max(0, targetWidth)

  while (active.length > 0) {
    const totalWeight = active.reduce((sum, item) => sum + item.weight, 0)
    const useEqualWeights = totalWeight <= 0
    const proposed: { item: ColumnSizingItem; size: number }[] = []
    let clampedSizeTotal = 0
    let didClamp = false

    for (const item of active) {
      const ratio = useEqualWeights ? 1 / active.length : item.weight / totalWeight
      const rawSize = remaining * ratio

      if (rawSize < item.min) {
        sizes.set(item.id, item.min)
        clampedSizeTotal += item.min
        didClamp = true
        continue
      }

      if (typeof item.max === 'number' && rawSize > item.max) {
        sizes.set(item.id, item.max)
        clampedSizeTotal += item.max
        didClamp = true
        continue
      }

      proposed.push({ item, size: rawSize })
    }

    if (!didClamp) {
      for (const { item, size } of proposed) {
        sizes.set(item.id, size)
      }
      break
    }

    remaining -= clampedSizeTotal
    active = proposed.map(({ item }) => item)
  }

  return sizes
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const getInitialState = (): TableState => ({
  sorting: [],
  columnFilters: [],
  globalFilter: '',
  pagination: { pageIndex: 0, pageSize: 10 },
  rowSelection: {},
  cellSelection: { range: null, anchor: null, isDragging: false },
  columnVisibility: {},
  columnOrder: [],
  columnPinning: { left: [], right: [] },
  columnSizing: {},
  columnSizingInfo: {
    isResizingColumn: false,
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    columnSizingStart: [],
  },
  expanded: {},
  rowPinning: { top: [], bottom: [] },
  grouping: [],
  editing: { activeCell: undefined, pendingValues: {} },
  commits: { cells: {}, nextOpId: 1 },
  keyboardNavigation: { focusedCell: null },
  undoRedo: { undoStack: [], redoStack: [], maxSize: 50 },
  fillHandle: { isDragging: false },
  formulas: { enabled: false, formulas: {}, computedValues: {}, errors: {} },
  rowDrag: { draggingRowId: null, overRowId: null, dropPosition: null },
  pivot: {
    enabled: false,
    config: { rowFields: [], columnFields: [], valueFields: [] },
    expandedRowGroups: {},
    expandedColumnGroups: {},
  },
})

// ---------------------------------------------------------------------------
// Locale support on Table interface (augmentation)
// ---------------------------------------------------------------------------

// (locale support is added directly to the table instance below)

// ---------------------------------------------------------------------------
// createTable
// ---------------------------------------------------------------------------

export function createTable<TData extends RowData>(options: TableOptions<TData>): Table<TData> {
  // T1-05: Guard data
  const safeData = options.data ?? []
  const safeColumns = options.columns ?? []

  const events = new EventEmitterImpl<YableEventMap<TData>>()

  // Resolve options with defaults
  const resolvedOptions: TableOptionsResolved<TData> = {
    state: {},
    onStateChange: () => {},
    renderFallbackValue: null,
    enableSorting: true,
    enableFilters: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSubRowSelection: true,
    enableRowClickSelection: false,
    enableCellSelection: true,
    enableRowPinning: false,
    enableColumnResizing: true,
    enableColumnReorder: true,
    enableHiding: true,
    enableGrouping: false,
    enableExpanding: true,
    enableKeyboardNavigation: true,
    manualSorting: false,
    manualFiltering: false,
    manualPagination: false,
    manualGrouping: false,
    manualExpanding: false,
    autoResetPageIndex: true,
    sortDescFirst: false,
    enableMultiSort: true,
    enableSortingRemoval: true,
    maxMultiSortColCount: Number.MAX_SAFE_INTEGER,
    isMultiSortEvent: (e: unknown) => {
      if (e && typeof e === 'object' && 'shiftKey' in e) {
        return !!(e as { shiftKey: boolean }).shiftKey
      }
      return false
    },
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    debugAll: false,
    debugHeaders: false,
    debugTable: false,
    debugColumns: false,
    debugRows: false,
    ...options,
    data: safeData,
    columns: safeColumns,
  } as TableOptionsResolved<TData>

  // Internal state (used when no external state is provided)
  let _internalState: TableState = getInitialState()

  // T1-10: per-instance warning flag
  let _largeDatasetWarned = false

  // Column drag state — mutable flag, not part of serializable TableState
  let _columnDragActive = false

  // ---------------------------------------------------------------------------
  // Table Instance
  // ---------------------------------------------------------------------------

  const table: Table<TData> = {
    options: resolvedOptions,

    // State
    getState: (): TableState => {
      return {
        ...getInitialState(),
        ..._internalState,
        ...resolvedOptions.state,
      }
    },
    setState: (updater: Updater<TableState>) => {
      const newState = functionalUpdate(updater, table.getState())
      _internalState = newState
      resolvedOptions.onStateChange(updater)
    },
    setOptions: (newOpts: Updater<TableOptionsResolved<TData>>) => {
      const updated = functionalUpdate(newOpts, resolvedOptions)
      Object.assign(resolvedOptions, updated)
    },
    reset: () => {
      _internalState = getInitialState()
      resolvedOptions.onStateChange(() => getInitialState())
    },

    // Column API — populated below
    getAllColumns: (): Column<TData, unknown>[] => [],
    getAllFlatColumns: (): Column<TData, unknown>[] => [],
    getAllLeafColumns: (): Column<TData, unknown>[] => [],
    getColumn: (_columnId: string): Column<TData, unknown> | undefined => undefined,
    getVisibleFlatColumns: (): Column<TData, unknown>[] => [],
    getVisibleLeafColumns: (): Column<TData, unknown>[] => [],
    getLeftVisibleLeafColumns: (): Column<TData, unknown>[] => [],
    getRightVisibleLeafColumns: (): Column<TData, unknown>[] => [],
    getCenterVisibleLeafColumns: (): Column<TData, unknown>[] => [],

    // Header API — populated below
    getHeaderGroups: (): HeaderGroup<TData>[] => [],
    getLeftHeaderGroups: (): HeaderGroup<TData>[] => [],
    getRightHeaderGroups: (): HeaderGroup<TData>[] => [],
    getCenterHeaderGroups: (): HeaderGroup<TData>[] => [],
    getFooterGroups: (): HeaderGroup<TData>[] => [],
    getLeftFooterGroups: (): HeaderGroup<TData>[] => [],
    getRightFooterGroups: (): HeaderGroup<TData>[] => [],
    getCenterFooterGroups: (): HeaderGroup<TData>[] => [],

    // Row Model API — populated below
    getCoreRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getRow: (id: string, _searchAll?: boolean): Row<TData> => {
      throw new Error(`[yable E003] Row with id "${id}" not found.`)
    },
    getFilteredRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getPreFilteredRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getSortedRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getPreSortedRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getPaginationRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getPrePaginationRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getGroupedRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getPreGroupedRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
    getPreExpandedRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),

    // Pagination
    getPageCount: (): number => {
      const state = table.getState()
      if (resolvedOptions.pageCount !== undefined) return resolvedOptions.pageCount
      const rowModel = table.getPrePaginationRowModel()
      return Math.ceil(rowModel.rows.length / state.pagination.pageSize)
    },
    getCanPreviousPage: (): boolean => table.getState().pagination.pageIndex > 0,
    getCanNextPage: (): boolean => {
      const state = table.getState()
      return state.pagination.pageIndex < table.getPageCount() - 1
    },
    previousPage: () => {
      table.setPageIndex((old: number) => Math.max(old - 1, 0))
    },
    nextPage: () => {
      table.setPageIndex((old: number) => Math.min(old + 1, table.getPageCount() - 1))
    },
    firstPage: () => table.setPageIndex(0),
    lastPage: () => table.setPageIndex(table.getPageCount() - 1),
    getRowCount: (): number => {
      return resolvedOptions.rowCount ?? table.getPrePaginationRowModel().rows.length
    },
    setPageIndex: (updater: Updater<number>) => {
      table.setPagination((old: PaginationState) => {
        const newPageIndex = functionalUpdate(updater, old.pageIndex)
        return { ...old, pageIndex: newPageIndex }
      })
    },
    setPageSize: (size: number) => {
      table.setPagination((old: PaginationState) => ({ ...old, pageSize: size, pageIndex: 0 }))
    },
    resetPageIndex: (defaultState?: boolean) => {
      table.setPageIndex(defaultState ? 0 : table.getState().pagination.pageIndex)
    },
    resetPageSize: (defaultState?: boolean) => {
      table.setPageSize(defaultState ? 10 : table.getState().pagination.pageSize)
    },
    resetPagination: (defaultState?: boolean) => {
      table.setPagination(
        defaultState ? { pageIndex: 0, pageSize: 10 } : table.getState().pagination,
      )
    },

    // Sorting API (placeholder — wired below)
    setSorting: (_updater: Updater<SortingState>) => {},
    resetSorting: (defaultState?: boolean) => {
      table.setSorting(defaultState ? [] : table.getState().sorting)
    },

    // Filtering API (placeholder — wired below)
    setColumnFilters: (_updater: Updater<ColumnFiltersState>) => {},
    resetColumnFilters: (defaultState?: boolean) => {
      table.setColumnFilters(defaultState ? [] : table.getState().columnFilters)
    },
    setGlobalFilter: (_updater: Updater<string>) => {},
    resetGlobalFilter: (defaultState?: boolean) => {
      table.setGlobalFilter(defaultState ? '' : table.getState().globalFilter)
    },

    // Selection API
    getSelectedRowModel: (): RowModel<TData> => {
      const selection = table.getState().rowSelection
      const rowModel = table.getRowModel()
      const rows = rowModel.flatRows.filter((row: Row<TData>) => selection[row.id])
      return {
        rows,
        flatRows: rows,
        rowsById: Object.fromEntries(rows.map((r: Row<TData>) => [r.id, r])),
      }
    },
    getFilteredSelectedRowModel: (): RowModel<TData> => table.getSelectedRowModel(),
    getGroupedSelectedRowModel: (): RowModel<TData> => table.getSelectedRowModel(),
    getIsAllRowsSelected: (): boolean => {
      const rowModel = table.getRowModel()
      const selection = table.getState().rowSelection
      // Only selectable rows count — synthetic group headers are never selected.
      const selectable = rowModel.flatRows.filter((row: Row<TData>) => row.getCanSelect())
      return selectable.length > 0 && selectable.every((row: Row<TData>) => selection[row.id])
    },
    getIsSomeRowsSelected: (): boolean => {
      const selection = table.getState().rowSelection
      return Object.keys(selection).length > 0 && !table.getIsAllRowsSelected()
    },
    getIsAllPageRowsSelected: (): boolean => {
      const rowModel = table.getRowModel()
      const selection = table.getState().rowSelection
      const selectable = rowModel.rows.filter((row: Row<TData>) => row.getCanSelect())
      return selectable.length > 0 && selectable.every((row: Row<TData>) => selection[row.id])
    },
    getIsSomePageRowsSelected: (): boolean => {
      const rowModel = table.getRowModel()
      const selection = table.getState().rowSelection
      return (
        rowModel.rows.some((row: Row<TData>) => selection[row.id]) &&
        !table.getIsAllPageRowsSelected()
      )
    },
    toggleAllRowsSelected: (value?: boolean) => {
      const rowModel = table.getPrePaginationRowModel()
      table.setRowSelection((old: RowSelectionState) => {
        const next = { ...old }
        const shouldSelect = value ?? !table.getIsAllRowsSelected()
        for (const row of rowModel.flatRows) {
          // Skip rows that aren't selectable (e.g. synthetic group headers) so
          // select-all never writes unselectable ids into the selection state.
          if (!row.getCanSelect()) continue
          if (shouldSelect) {
            next[row.id] = true
          } else {
            delete next[row.id]
          }
        }
        return next
      })
    },
    toggleAllPageRowsSelected: (value?: boolean) => {
      const rowModel = table.getRowModel()
      table.setRowSelection((old: RowSelectionState) => {
        const next = { ...old }
        const shouldSelect = value ?? !table.getIsAllPageRowsSelected()
        for (const row of rowModel.rows) {
          if (!row.getCanSelect()) continue
          if (shouldSelect) {
            next[row.id] = true
          } else {
            delete next[row.id]
          }
        }
        return next
      })
    },
    getToggleAllRowsSelectedHandler: () => {
      return (_e: unknown) => {
        table.toggleAllRowsSelected()
      }
    },
    getToggleAllPageRowsSelectedHandler: () => {
      return (_e: unknown) => {
        table.toggleAllPageRowsSelected()
      }
    },
    setRowSelection: (_updater: Updater<RowSelectionState>) => {},
    resetRowSelection: (defaultState?: boolean) => {
      table.setRowSelection(defaultState ? {} : table.getState().rowSelection)
    },
    getCellSelectionRange: (): CellRange | null => table.getState().cellSelection?.range ?? null,
    clearCellSelection: () => {
      table.setState((old: TableState) => ({
        ...old,
        cellSelection: {
          ...(old.cellSelection ?? { range: null, anchor: null, isDragging: false }),
          range: null,
          isDragging: false,
        },
      }))
    },
    selectCell: (
      cell: KeyboardNavigationCell,
      options?: { extend?: boolean; keepAnchor?: boolean },
    ) => {
      const clamped = clampCellToTable(table, cell)
      if (!clamped) {
        table.clearCellSelection()
        return
      }

      table.setState((old: TableState) => {
        const previousSelection = old.cellSelection ?? {
          range: null,
          anchor: null,
          isDragging: false,
        }
        const anchor =
          options?.extend && previousSelection.anchor
            ? previousSelection.anchor
            : options?.keepAnchor
              ? (previousSelection.anchor ?? clamped)
              : clamped

        return {
          ...old,
          cellSelection: {
            range: createCellRange(anchor, clamped),
            anchor,
            isDragging: false,
          },
        }
      })
    },
    startCellRangeSelection: (cell: KeyboardNavigationCell, options?: { extend?: boolean }) => {
      const clamped = clampCellToTable(table, cell)
      if (!clamped) {
        table.clearCellSelection()
        return
      }

      table.setState((old: TableState) => {
        const previousSelection = old.cellSelection ?? {
          range: null,
          anchor: null,
          isDragging: false,
        }
        const anchor =
          options?.extend && previousSelection.anchor ? previousSelection.anchor : clamped

        return {
          ...old,
          cellSelection: {
            range: createCellRange(anchor, clamped),
            anchor,
            isDragging: true,
          },
        }
      })
    },
    updateCellRangeSelection: (cell: KeyboardNavigationCell) => {
      const clamped = clampCellToTable(table, cell)
      if (!clamped) return

      table.setState((old: TableState) => {
        const previousSelection = old.cellSelection ?? {
          range: null,
          anchor: null,
          isDragging: false,
        }
        if (!previousSelection.range && !previousSelection.isDragging) return old

        const anchor = previousSelection.anchor ?? clamped
        return {
          ...old,
          cellSelection: {
            ...previousSelection,
            range: createCellRange(anchor, clamped),
          },
        }
      })
    },
    endCellRangeSelection: () => {
      table.setState((old: TableState) => ({
        ...old,
        cellSelection: {
          ...(old.cellSelection ?? { range: null, anchor: null, isDragging: false }),
          isDragging: false,
        },
      }))
    },
    getIsCellSelected: (rowIndex: number, columnIndex: number) => {
      const range = table.getCellSelectionRange()
      return range ? isCellInRange(range, rowIndex, columnIndex) : false
    },
    getCellSelectionEdges: (rowIndex: number, columnIndex: number) => {
      const range = table.getCellSelectionRange()
      if (!range || !isCellInRange(range, rowIndex, columnIndex)) {
        return null
      }

      const normalized = normalizeCellRange(range)
      return {
        top: rowIndex === normalized.top,
        right: columnIndex === normalized.right,
        bottom: rowIndex === normalized.bottom,
        left: columnIndex === normalized.left,
      }
    },

    // Visibility API
    setColumnVisibility: (_updater: Updater<VisibilityState>) => {},
    resetColumnVisibility: (defaultState?: boolean) => {
      table.setColumnVisibility(defaultState ? {} : table.getState().columnVisibility)
    },
    toggleAllColumnsVisible: (value?: boolean) => {
      const allColumns = table.getAllLeafColumns()
      table.setColumnVisibility((old: VisibilityState) => {
        const next = { ...old }
        const shouldShow = value ?? !table.getIsAllColumnsVisible()
        for (const col of allColumns) {
          next[col.id] = shouldShow
        }
        return next
      })
    },
    getIsAllColumnsVisible: (): boolean => {
      return table.getAllLeafColumns().every((col: Column<TData, unknown>) => col.getIsVisible())
    },
    getIsSomeColumnsVisible: (): boolean => {
      return table.getAllLeafColumns().some((col: Column<TData, unknown>) => col.getIsVisible())
    },

    // Column Order API
    setColumnOrder: (_updater: Updater<ColumnOrderState>) => {},
    resetColumnOrder: (defaultState?: boolean) => {
      table.setColumnOrder(defaultState ? [] : table.getState().columnOrder)
    },

    // Column Pinning API
    setColumnPinning: (_updater: Updater<ColumnPinningState>) => {},
    resetColumnPinning: (defaultState?: boolean) => {
      table.setColumnPinning(
        defaultState ? { left: [], right: [] } : table.getState().columnPinning,
      )
    },
    getIsSomeColumnsPinned: (position?: 'left' | 'right'): boolean => {
      const pinning = table.getState().columnPinning
      if (position === 'left') return (pinning.left?.length ?? 0) > 0
      if (position === 'right') return (pinning.right?.length ?? 0) > 0
      return (pinning.left?.length ?? 0) > 0 || (pinning.right?.length ?? 0) > 0
    },

    // Column Sizing API
    setColumnSizing: (_updater: Updater<ColumnSizingState>) => {},
    setColumnSizingInfo: (_updater: Updater<ColumnSizingInfoState>) => {},
    resetColumnSizing: (defaultState?: boolean) => {
      table.setColumnSizing(defaultState ? {} : table.getState().columnSizing)
    },
    sizeColumnsToFit: (width: number) => {
      if (!Number.isFinite(width)) return

      const targetWidth = Math.max(0, width)
      const visibleColumns = table.getVisibleLeafColumns()
      if (visibleColumns.length === 0) return

      const nextSizing: ColumnSizingState = {}
      const flexItems: ColumnSizingItem[] = []
      let fixedTotal = 0

      for (const column of visibleColumns) {
        const flex = getColumnFlex(column)
        const currentSize = column.getSize()
        const bounds = getColumnSizingBounds(column)

        if (flex > 0) {
          flexItems.push({
            id: column.id,
            min: bounds.min,
            max: bounds.max,
            weight: flex,
          })
        } else {
          nextSizing[column.id] = currentSize
          fixedTotal += currentSize
        }
      }

      if (flexItems.length > 0) {
        const distributed = distributeColumnSizes(flexItems, targetWidth - fixedTotal)
        for (const [id, size] of distributed) {
          nextSizing[id] = size
        }
      } else {
        const scaledItems = visibleColumns.map((column): ColumnSizingItem => {
          const currentSize = column.getSize()
          const bounds = getColumnSizingBounds(column)
          return {
            id: column.id,
            min: bounds.min,
            max: bounds.max,
            weight: currentSize > 0 ? currentSize : 1,
          }
        })
        const distributed = distributeColumnSizes(scaledItems, targetWidth)
        for (const [id, size] of distributed) {
          nextSizing[id] = size
        }
      }

      table.setColumnSizing((old) => ({
        ...old,
        ...nextSizing,
      }))
    },
    getTotalSize: (): number => {
      return table
        .getVisibleFlatColumns()
        .reduce((sum: number, col: Column<TData, unknown>) => sum + col.getSize(), 0)
    },
    getLeftTotalSize: (): number => {
      return table
        .getLeftVisibleLeafColumns()
        .reduce((sum: number, col: Column<TData, unknown>) => sum + col.getSize(), 0)
    },
    getRightTotalSize: (): number => {
      return table
        .getRightVisibleLeafColumns()
        .reduce((sum: number, col: Column<TData, unknown>) => sum + col.getSize(), 0)
    },
    getCenterTotalSize: (): number => {
      return table
        .getCenterVisibleLeafColumns()
        .reduce((sum: number, col: Column<TData, unknown>) => sum + col.getSize(), 0)
    },

    // Expanding API
    setExpanded: (_updater: Updater<ExpandedState>) => {},
    resetExpanded: (defaultState?: boolean) => {
      table.setExpanded(defaultState ? {} : table.getState().expanded)
    },
    toggleAllRowsExpanded: (expanded?: boolean) => {
      const shouldExpand = expanded ?? !table.getIsAllRowsExpanded()
      if (shouldExpand) {
        const rowModel = table.getPreExpandedRowModel()
        const expandedState: Record<string, boolean> = {}
        for (const row of rowModel.flatRows) {
          if (row.subRows.length > 0) {
            expandedState[row.id] = true
          }
        }
        table.setExpanded(expandedState)
      } else {
        table.setExpanded({})
      }
    },
    getCanSomeRowsExpand: (): boolean => {
      return table.getPreExpandedRowModel().flatRows.some((r: Row<TData>) => r.getCanExpand())
    },
    getIsAllRowsExpanded: (): boolean => {
      const rowModel = table.getPreExpandedRowModel()
      const expanded = table.getState().expanded
      if (expanded === true) return true
      const expandableRows = rowModel.flatRows.filter((r: Row<TData>) => r.subRows.length > 0)
      if (expandableRows.length === 0) return false
      const expandedRecord = expanded as Record<string, boolean>
      return expandableRows.every((r: Row<TData>) => expandedRecord[r.id])
    },
    getIsSomeRowsExpanded: (): boolean => {
      const expanded = table.getState().expanded
      if (expanded === true) return true
      return Object.keys(expanded as Record<string, boolean>).length > 0
    },
    getExpandedDepth: (): number => {
      const expanded = table.getState().expanded
      if (expanded === true) return Infinity
      let maxDepth = 0
      const expandedRecord = expanded as Record<string, boolean>
      const rowModel = table.getPreExpandedRowModel()
      for (const row of rowModel.flatRows) {
        if (expandedRecord[row.id]) {
          maxDepth = Math.max(maxDepth, row.depth)
        }
      }
      return maxDepth
    },
    getExpandedRowModel: (): RowModel<TData> => table.getCoreRowModel(),

    // Row Pinning API
    setRowPinning: (_updater: Updater<RowPinningState>) => {},
    resetRowPinning: (defaultState?: boolean) => {
      table.setRowPinning(defaultState ? { top: [], bottom: [] } : table.getState().rowPinning)
    },
    getTopRows: (): Row<TData>[] => {
      const pinning = table.getState().rowPinning
      const rowModel = table.getCoreRowModel()
      return (pinning.top ?? [])
        .map((id: string) => rowModel.rowsById[id])
        .filter((row: Row<TData> | undefined): row is Row<TData> => row !== undefined)
    },
    getBottomRows: (): Row<TData>[] => {
      const pinning = table.getState().rowPinning
      const rowModel = table.getCoreRowModel()
      return (pinning.bottom ?? [])
        .map((id: string) => rowModel.rowsById[id])
        .filter((row: Row<TData> | undefined): row is Row<TData> => row !== undefined)
    },
    getCenterRows: (): Row<TData>[] => {
      const pinning = table.getState().rowPinning
      const rowModel = table.getRowModel()
      const pinned = new Set([...(pinning.top ?? []), ...(pinning.bottom ?? [])])
      return rowModel.rows.filter((row: Row<TData>) => !pinned.has(row.id))
    },

    // Grouping API
    setGrouping: (_updater: Updater<GroupingState>) => {},
    resetGrouping: (defaultState?: boolean) => {
      table.setGrouping(defaultState ? [] : table.getState().grouping)
    },

    // Internal — T1-11: _features kept on the interface for forward-compatibility
    // but not wired to a plugin system yet
    _features: [],
    initialState: getInitialState(),

    // Editing API
    setEditing: (_updater: Updater<EditingState>) => {},
    startEditing: (rowId: string, columnId: string) => {
      const focusedCell = getCellPositionByIds(table, rowId, columnId)
      if (focusedCell) {
        table.setFocusedCell(focusedCell)
      }

      table.setEditing((old: EditingState) => ({
        ...old,
        activeCell: { rowId, columnId },
      }))
    },
    commitEdit: () => {
      const editing = table.getState().editing
      if (!editing?.activeCell) return

      const { rowId, columnId } = editing.activeCell

      // Capture cursor position so we can keep focus on the cell after commit
      const focusedCell = getCellPositionByIds(table, rowId, columnId)

      // Read the pending value (may be undefined if user pressed Enter without changes)
      const pendingValue = editing.pendingValues?.[rowId]?.[columnId]
      const hasPending = pendingValue !== undefined

      // Clear active cell first so the input unmounts. Also drop the pending
      // value for this cell since the coordinator now owns it.
      table.setEditing((old: EditingState) => {
        const nextPending: Record<string, Record<string, unknown>> = {
          ...(old.pendingValues ?? {}),
        }
        if (hasPending && nextPending[rowId]) {
          const row: Record<string, unknown> = { ...nextPending[rowId] }
          delete row[columnId]
          if (Object.keys(row).length === 0) {
            delete nextPending[rowId]
          } else {
            nextPending[rowId] = row
          }
        }
        return {
          ...old,
          activeCell: undefined,
          pendingValues: nextPending,
        }
      })

      if (focusedCell) {
        table.setFocusedCell(focusedCell)
      }

      // Per-column commit hooks fire for every committed cell, independent of
      // the table-level onCommit/onEditCommit handler below.
      if (hasPending) {
        fireColumnCommitHooks(table, { [rowId]: { [columnId]: pendingValue } })
      }

      // If onCommit is defined and autoCommit !== false, dispatch through the
      // coordinator. Otherwise (legacy mode), fire the existing onEditCommit hook.
      const opts = table.options
      const autoCommit = opts.autoCommit !== false
      if (hasPending && opts.onCommit && autoCommit) {
        let previousValue: unknown
        try {
          previousValue = table.getRow(rowId, true).getValue(columnId)
        } catch {
          previousValue = undefined
        }
        // Fire-and-forget — coordinator updates the slice asynchronously,
        // and the table re-renders via state changes
        void commitCoordinatorMap
          .get(table as Table<RowData>)
          ?.dispatch([{ rowId, columnId, value: pendingValue, previousValue }])
      } else if (hasPending && opts.onEditCommit) {
        // Legacy fallback
        opts.onEditCommit({ [rowId]: { [columnId]: pendingValue } as Partial<TData> })
      }
    },
    cancelEdit: () => {
      const editing = table.getState().editing
      const focusedCell = editing?.activeCell
        ? getCellPositionByIds(table, editing.activeCell.rowId, editing.activeCell.columnId)
        : null

      table.setEditing((old: EditingState) => ({
        ...old,
        activeCell: undefined,
      }))

      if (focusedCell) {
        table.setFocusedCell(focusedCell)
      }
    },
    resetEditing: (defaultState?: boolean) => {
      table.setEditing(
        defaultState ? { activeCell: undefined, pendingValues: {} } : table.getState().editing,
      )
    },
    setPendingValue: (rowId: string, columnId: string, value: unknown) => {
      table.setEditing((old: EditingState) => ({
        ...old,
        pendingValues: {
          ...old.pendingValues,
          [rowId]: {
            ...(old.pendingValues[rowId] ?? {}),
            [columnId]: value,
          },
        },
      }))
    },
    getPendingValue: (rowId: string, columnId: string): unknown | undefined => {
      const editing = table.getState().editing
      return editing.pendingValues?.[rowId]?.[columnId]
    },
    getPendingRow: (rowId: string): Partial<TData> | undefined => {
      const editing = table.getState().editing
      return editing.pendingValues?.[rowId] as Partial<TData> | undefined
    },
    getAllPendingChanges: (): Record<string, Partial<TData>> => {
      const editing = table.getState().editing
      return (editing.pendingValues ?? {}) as Record<string, Partial<TData>>
    },
    hasPendingChanges: (): boolean => {
      return Object.keys(table.getAllPendingChanges()).length > 0
    },
    isValid: (): boolean => {
      return Object.keys(table.getValidationErrors()).length === 0
    },
    commitAllPending: () => {
      const changes = table.getAllPendingChanges()
      fireColumnCommitHooks(table, changes)
      resolvedOptions.onEditCommit?.(changes)
      table.setEditing((old: EditingState) => ({
        ...old,
        pendingValues: {},
      }))
    },
    discardAllPending: () => {
      table.setEditing((old: EditingState) => ({
        ...old,
        pendingValues: {},
      }))
    },
    getValidationErrors: (): Record<string, Record<string, string>> => {
      // Compute validation errors on the fly from the current pending edits by
      // running each column's editConfig.validate against its pending value.
      const result: Record<string, Record<string, string>> = {}
      try {
        const pendingValues = table.getState().editing?.pendingValues ?? {}

        for (const rowId of Object.keys(pendingValues)) {
          const rowPending = pendingValues[rowId]
          if (!rowPending) continue

          // Resolve the row once per pending row; validators receive it. If the
          // row no longer exists, skip validation rather than throwing.
          let row: Row<TData> | undefined
          try {
            row = table.getRow(rowId, true)
          } catch {
            row = undefined
          }
          if (!row) continue

          for (const columnId of Object.keys(rowPending)) {
            const column = table.getColumn(columnId)
            if (!column) continue

            const editConfig = (
              column.columnDef as ColumnDef<TData> & Partial<ColumnDefExtensions<TData>>
            ).editConfig
            const validate = editConfig?.validate
            if (!validate) continue

            try {
              const error = validate(rowPending[columnId], row)
              if (error) {
                if (!result[rowId]) result[rowId] = {}
                result[rowId]![columnId] = error
              }
            } catch (err) {
              console.error(
                `[yable] editConfig.validate threw for column "${columnId}", row "${rowId}":`,
                err,
              )
            }
          }
        }
      } catch (err) {
        console.error('[yable] getValidationErrors failed:', err)
      }
      return result
    },

    // Async commit API (Task #10) — wired below after the literal
    getCellRenderValue: () => undefined,
    getCellStatus: () => 'idle',
    getCellErrorMessage: () => undefined,
    getCellConflictWith: () => undefined,
    commit: async () => {},
    retryCommit: async () => {},
    dismissCommit: () => {},
    dismissAllCommits: () => {},

    // Keyboard Navigation API
    setKeyboardNavigation: (_updater: Updater<KeyboardNavigationState>) => {},
    getFocusedCell: () => {
      return normalizeFocusedCell(table, table.getState().keyboardNavigation?.focusedCell)
    },
    setFocusedCell: (cell: KeyboardNavigationCell | null) => {
      const normalized = normalizeFocusedCell(table, cell)
      table.setKeyboardNavigation((old: KeyboardNavigationState) => ({
        ...old,
        focusedCell: normalized,
      }))
    },
    clearFocusedCell: () => {
      table.setKeyboardNavigation((old: KeyboardNavigationState) => ({
        ...old,
        focusedCell: null,
      }))
    },
    moveFocus: (action: KeyboardNavigationAction) => {
      const nextCell = getNextFocusedCell(table, table.getFocusedCell(), action)
      table.setFocusedCell(nextCell)
      return nextCell
    },
    resetKeyboardNavigation: (defaultState?: boolean) => {
      table.setKeyboardNavigation(
        defaultState ? { focusedCell: null } : table.getState().keyboardNavigation,
      )
    },

    // Clipboard API — wired below after row/column models are available
    copyToClipboard: () => '',
    pasteFromClipboard: () => {},
    cutCells: () => '',

    // Export API
    exportData: (opts?: ExportOptions): string => {
      const rowModel = opts?.allRows ? table.getPrePaginationRowModel() : table.getRowModel()
      const columns = opts?.columns
        ? table
            .getAllLeafColumns()
            .filter((col: Column<TData, unknown>) => opts.columns!.includes(col.id))
        : table.getVisibleLeafColumns()

      if (opts?.format === 'json' || !opts?.format) {
        const data = rowModel.rows.map((row: Row<TData>) => {
          const obj: Record<string, unknown> = {}
          for (const col of columns) {
            obj[col.id] = row.getValue(col.id)
          }
          return obj
        })
        return JSON.stringify(data, null, 2)
      }

      if (opts?.format === 'csv') {
        const headers = columns.map((col: Column<TData, unknown>) => {
          const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id
          return escapeCSV(header)
        })

        const rows = rowModel.rows.map((row: Row<TData>) =>
          columns
            .map((col: Column<TData, unknown>) => escapeCSV(String(row.getValue(col.id) ?? '')))
            .join(','),
        )

        return [headers.join(','), ...rows].join('\n')
      }

      return ''
    },

    // Pagination state setters (placeholder — wired below)
    setPagination: (_updater: Updater<PaginationState>) => {},

    // Events
    events,

    // T1-06: Locale support
    getLocaleString: (key: string): string => {
      // Check for per-table locale option first
      const tableLocale = (
        resolvedOptions as TableOptionsResolved<TData> & { locale?: Partial<YableLocale> }
      ).locale
      if (tableLocale && key in tableLocale) {
        const value = (tableLocale as Record<string, string>)[key]
        if (value !== undefined) return value
      }
      // Fall back to global default locale
      return (getDefaultLocale() as unknown as Record<string, string>)[key] ?? key
    },

    // Column Drag State (used by suppressDragHidesColumns)
    setColumnDragActive: (active: boolean) => {
      _columnDragActive = active
    },
    getIsColumnDragActive: () => _columnDragActive,

    // Smart column width — signal any active `autoColumnWidth` sizing (wired in
    // the React layer via `table.events`) to re-measure now.
    remeasureColumns: (reason?: string) => {
      events.emit('columns:remeasure', { reason })
    },

    // Stubs for externally-wired features — filled in by integration functions
    // (undoRedo, fillHandle, formulas, rowDragging, fullRowEditing, pivot)
    undo: () => {},
    redo: () => {},
    canUndo: () => false,
    canRedo: () => false,
    clearUndoHistory: () => {},
    fillRange: () => {},
    setFormula: () => {},
    getFormula: () => undefined,
    evaluateFormulas: () => {},
    moveRow: () => {},
    startRowEditing: () => {},
    commitRowEdit: () => {},
    cancelRowEdit: () => {},
    isRowEditing: () => false,
    getEditingRows: () => [],
    getEditableColumnIds: () => [],
    getPivotRowModel: (): RowModel<TData> => ({ rows: [], flatRows: [], rowsById: {} }),
  }

  // ---------------------------------------------------------------------------
  // Wire up state updaters to use table instance
  // ---------------------------------------------------------------------------

  // makeStateUpdater requires loosely-typed setState/getState/options due to its
  // mapped-type signature. Cast the table to satisfy the parameter shape.
  const wireUpdater = <V>(key: keyof TableState) => {
    return makeStateUpdater(key, table as Parameters<typeof makeStateUpdater>[1]) as (
      updater: Updater<V>,
    ) => void
  }

  table.setSorting = wireUpdater<SortingState>('sorting')
  table.setColumnFilters = wireUpdater<ColumnFiltersState>('columnFilters')
  table.setGlobalFilter = wireUpdater<string>('globalFilter')
  const setRowSelectionBase = wireUpdater<RowSelectionState>('rowSelection')
  table.setRowSelection = (updater: Updater<RowSelectionState>) => {
    const previous = table.getState().rowSelection
    const next = functionalUpdate(updater, previous)
    setRowSelectionBase(next)
    table.events.emit('selection:change', {
      selection: next,
      selectedRows: table
        .getPrePaginationRowModel()
        .flatRows.filter((row: Row<TData>) => next[row.id]),
    })
  }
  table.setColumnVisibility = wireUpdater<VisibilityState>('columnVisibility')
  table.setColumnOrder = wireUpdater<ColumnOrderState>('columnOrder')
  table.setColumnPinning = wireUpdater<ColumnPinningState>('columnPinning')
  table.setColumnSizing = wireUpdater<ColumnSizingState>('columnSizing')
  table.setColumnSizingInfo = wireUpdater<ColumnSizingInfoState>('columnSizingInfo')
  table.setExpanded = wireUpdater<ExpandedState>('expanded')
  table.setRowPinning = wireUpdater<RowPinningState>('rowPinning')
  table.setGrouping = wireUpdater<GroupingState>('grouping')
  table.setEditing = wireUpdater<EditingState>('editing')
  table.setKeyboardNavigation = wireUpdater<KeyboardNavigationState>('keyboardNavigation')
  table.setPagination = wireUpdater<PaginationState>('pagination')

  // ---------------------------------------------------------------------------
  // Column Processing — T1-01: Build columnMap for O(1) lookup
  // ---------------------------------------------------------------------------

  const processColumns = memo(
    () => [resolvedOptions.columns, resolvedOptions.state?.columnOrder],
    (columnDefs: ColumnDef<TData>[]) => {
      const allColumns: Column<TData, unknown>[] = []
      const allFlatColumns: Column<TData, unknown>[] = []
      const columnMap = new Map<string, Column<TData, unknown>>()

      const processColumnDef = (
        colDef: ColumnDef<TData>,
        depth: number,
        parent?: Column<TData, unknown>,
      ): Column<TData, unknown> => {
        // Merge defaultColumnDef under the column def — column-specific values win
        const mergedColDef = resolvedOptions.defaultColumnDef
          ? ({ ...resolvedOptions.defaultColumnDef, ...colDef } as ColumnDef<TData>)
          : colDef
        const column = createColumn(table, mergedColDef, depth, parent)
        allColumns.push(column)
        columnMap.set(column.id, column)

        if ('columns' in colDef && colDef.columns?.length) {
          column.columns = colDef.columns.map((subDef: ColumnDef<TData>) =>
            processColumnDef(subDef, depth + 1, column),
          )
        } else {
          allFlatColumns.push(column)
        }

        return column
      }

      const topLevel = columnDefs.map((def: ColumnDef<TData>) => processColumnDef(def, 0))

      return {
        topLevel,
        allColumns,
        allFlatColumns,
        allLeafColumns: allFlatColumns,
        columnMap,
      }
    },
    { key: 'processColumns' },
  )

  table.getAllColumns = () => processColumns().allColumns
  table.getAllFlatColumns = () => processColumns().allFlatColumns
  table.getAllLeafColumns = () => processColumns().allLeafColumns
  // T1-01: O(1) column lookup via Map instead of linear .find()
  table.getColumn = (id: string) => processColumns().columnMap.get(id)

  // Visible columns with ordering and pinning
  table.getVisibleFlatColumns = memo(
    () => [
      table.getAllLeafColumns(),
      table.getState().columnVisibility,
      table.getState().columnOrder,
    ],
    (allLeaf: Column<TData, unknown>[]) => {
      let cols = allLeaf.filter((col: Column<TData, unknown>) => col.getIsVisible())
      const order = table.getState().columnOrder
      if (order.length) {
        const orderMap = new Map<string, number>(order.map((id: string, i: number) => [id, i]))
        cols = cols.sort((a: Column<TData, unknown>, b: Column<TData, unknown>) => {
          const ai: number = orderMap.get(a.id) ?? Infinity
          const bi: number = orderMap.get(b.id) ?? Infinity
          return ai - bi
        })
      }
      return cols
    },
    { key: 'getVisibleFlatColumns' },
  )

  table.getVisibleLeafColumns = table.getVisibleFlatColumns

  table.getLeftVisibleLeafColumns = memo(
    () => [table.getVisibleFlatColumns(), table.getState().columnPinning],
    (visible: Column<TData, unknown>[]) => {
      const pinned = table.getState().columnPinning.left ?? []
      return visible.filter((col) => pinned.includes(col.id))
    },
    { key: 'getLeftVisibleLeafColumns' },
  )

  table.getRightVisibleLeafColumns = memo(
    () => [table.getVisibleFlatColumns(), table.getState().columnPinning],
    (visible: Column<TData, unknown>[]) => {
      const pinned = table.getState().columnPinning.right ?? []
      return visible.filter((col) => pinned.includes(col.id))
    },
    { key: 'getRightVisibleLeafColumns' },
  )

  table.getCenterVisibleLeafColumns = memo(
    () => [table.getVisibleFlatColumns(), table.getState().columnPinning],
    (visible: Column<TData, unknown>[]) => {
      const left = new Set(table.getState().columnPinning.left ?? [])
      const right = new Set(table.getState().columnPinning.right ?? [])
      return visible.filter((col) => !left.has(col.id) && !right.has(col.id))
    },
    { key: 'getCenterVisibleLeafColumns' },
  )

  // ---------------------------------------------------------------------------
  // Header Groups
  // ---------------------------------------------------------------------------

  table.getHeaderGroups = memo(
    () => [
      table.getAllColumns(),
      table.getState().columnVisibility,
      table.getState().columnPinning,
      table.getState().columnOrder,
    ],
    () => buildHeaderGroups(table, processColumns().topLevel),
    { key: 'getHeaderGroups' },
  )

  table.getLeftHeaderGroups = memo(
    () => [table.getHeaderGroups(), table.getState().columnPinning],
    (groups: HeaderGroup<TData>[]) => {
      const leftIds = new Set(table.getState().columnPinning.left ?? [])
      if (leftIds.size === 0) return []
      return groups.map((group: HeaderGroup<TData>) => ({
        ...group,
        headers: group.headers.filter((h: Header<TData, unknown>) => leftIds.has(h.column.id)),
      }))
    },
    { key: 'getLeftHeaderGroups' },
  )

  table.getRightHeaderGroups = memo(
    () => [table.getHeaderGroups(), table.getState().columnPinning],
    (groups: HeaderGroup<TData>[]) => {
      const rightIds = new Set(table.getState().columnPinning.right ?? [])
      if (rightIds.size === 0) return []
      return groups.map((group: HeaderGroup<TData>) => ({
        ...group,
        headers: group.headers.filter((h: Header<TData, unknown>) => rightIds.has(h.column.id)),
      }))
    },
    { key: 'getRightHeaderGroups' },
  )

  table.getCenterHeaderGroups = memo(
    () => [table.getHeaderGroups(), table.getState().columnPinning],
    (groups: HeaderGroup<TData>[]) => {
      const leftIds = new Set(table.getState().columnPinning.left ?? [])
      const rightIds = new Set(table.getState().columnPinning.right ?? [])
      return groups.map((group: HeaderGroup<TData>) => ({
        ...group,
        headers: group.headers.filter(
          (h: Header<TData, unknown>) => !leftIds.has(h.column.id) && !rightIds.has(h.column.id),
        ),
      }))
    },
    { key: 'getCenterHeaderGroups' },
  )

  table.getFooterGroups = () => [...table.getHeaderGroups()].reverse()
  table.getLeftFooterGroups = () => [...table.getLeftHeaderGroups()].reverse()
  table.getRightFooterGroups = () => [...table.getRightHeaderGroups()].reverse()
  table.getCenterFooterGroups = () => [...table.getCenterHeaderGroups()].reverse()

  // ---------------------------------------------------------------------------
  // Row Model Pipeline
  // ---------------------------------------------------------------------------

  // 1. Core Row Model — raw data -> Row[]
  table.getCoreRowModel = memo(
    // Tree data re-flattens whenever the expanded set changes, so its memo key
    // includes `expanded`. The flat path keeps the original `[data]` key to
    // avoid re-running on unrelated expansion state.
    () =>
      resolvedOptions.treeData && resolvedOptions.getDataPath
        ? [resolvedOptions.data, table.getState().expanded]
        : [resolvedOptions.data],
    (data: TData[]) => {
      // T1-05: guard against null/undefined data
      const safeData = data ?? []

      // Tree data: build a hierarchical row model from `getDataPath`. Filter
      // and sort stages below re-enter the tree pipeline so hierarchy is
      // preserved instead of operating over already-flattened visible rows.
      if (resolvedOptions.treeData && resolvedOptions.getDataPath) {
        return getTreeRowModel(table, safeData, resolvedOptions.getDataPath)
      }

      // T1-10: Large dataset warning
      if (
        safeData.length > 10_000 &&
        !resolvedOptions.enableVirtualization &&
        !_largeDatasetWarned
      ) {
        _largeDatasetWarned = true
        console.warn(
          `Yable: Rendering ${safeData.length} rows without virtualization. Consider enabling virtualization for better performance.`,
        )
      }

      const rows: Row<TData>[] = safeData.map((original: TData, index: number) => {
        let id: string
        if (resolvedOptions.getRowId) {
          // T1-09: Wrap user callback in try-catch
          try {
            id = resolvedOptions.getRowId(original, index)
          } catch (err) {
            console.error(`[yable] getRowId threw for row at index ${index}:`, err)
            id = String(index)
          }
        } else {
          id = String(index)
        }

        return createRow(table, id, original, index, 0)
      })

      const flatRows = rows
      const rowsById: Record<string, Row<TData>> = {}
      for (const row of flatRows) {
        rowsById[row.id] = row
      }

      return { rows, flatRows, rowsById }
    },
    { key: 'getCoreRowModel' },
  )

  // 2. Pre-filter = core
  table.getPreFilteredRowModel = table.getCoreRowModel

  // 3. Filtered Row Model
  table.getFilteredRowModel = memo(
    () => [table.getCoreRowModel(), table.getState().columnFilters, table.getState().globalFilter],
    (coreModel: RowModel<TData>) => {
      if (resolvedOptions.manualFiltering) return coreModel

      const columnFilters = table.getState().columnFilters
      const globalFilter = table.getState().globalFilter

      if (columnFilters.length === 0 && !globalFilter) return coreModel

      if (resolvedOptions.treeData && resolvedOptions.getDataPath) {
        return getTreeRowModel(table, resolvedOptions.data ?? [], resolvedOptions.getDataPath, {
          filterNode: (node) =>
            rowPassesFilters(table, createTreeNodeRow(table, node), columnFilters, globalFilter),
        })
      }

      let filtered = coreModel.rows

      // Apply column filters
      filtered = filtered.filter((row: Row<TData>) =>
        rowPassesColumnFilters(table, row, columnFilters),
      )

      // Apply global filter
      filtered = filtered.filter((row: Row<TData>) =>
        rowPassesGlobalFilter(table, row, globalFilter),
      )

      const rowsById: Record<string, Row<TData>> = {}
      for (const row of filtered) {
        rowsById[row.id] = row
      }

      return { rows: filtered, flatRows: filtered, rowsById }
    },
    { key: 'getFilteredRowModel' },
  )

  // 4. Pre-sort = filtered
  table.getPreSortedRowModel = table.getFilteredRowModel

  // 5. Sorted Row Model
  table.getSortedRowModel = memo(
    () => [table.getFilteredRowModel(), table.getState().sorting],
    (filteredModel: RowModel<TData>) => {
      // `postSortRows` (AG-parity) may reorder the final rows even when no
      // column sorting is active — e.g. keeping child rows under their parents.
      const postSortRows = resolvedOptions.postSortRows
      const applyPostSort = (rows: Row<TData>[]): Row<TData>[] | null => {
        if (!postSortRows) return null
        const arr = rows.slice()
        const returned = postSortRows(arr)
        return Array.isArray(returned) ? returned : arr
      }

      const buildModel = (rows: Row<TData>[]): RowModel<TData> => {
        const rowsById: Record<string, Row<TData>> = {}
        for (const row of rows) {
          rowsById[row.id] = row
        }
        return { rows, flatRows: rows, rowsById }
      }

      if (resolvedOptions.manualSorting) return filteredModel

      const sorting = table.getState().sorting
      const hasSorting = sorting.length > 0

      if ((hasSorting || postSortRows) && resolvedOptions.treeData && resolvedOptions.getDataPath) {
        const columnFilters = table.getState().columnFilters
        const globalFilter = table.getState().globalFilter
        const hasFilters = columnFilters.length > 0 || Boolean(globalFilter)

        const treeModel = getTreeRowModel(
          table,
          resolvedOptions.data ?? [],
          resolvedOptions.getDataPath,
          {
            filterNode: hasFilters
              ? (node) =>
                  rowPassesFilters(
                    table,
                    createTreeNodeRow(table, node),
                    columnFilters,
                    globalFilter,
                  )
              : undefined,
            compareNodes: hasSorting ? createTreeNodeComparator(table, sorting) : undefined,
          },
        )
        const posted = applyPostSort(treeModel.rows)
        return posted ? buildModel(posted) : treeModel
      }

      if (!hasSorting) {
        const posted = applyPostSort(filteredModel.rows)
        return posted ? buildModel(posted) : filteredModel
      }

      const sorted = [...filteredModel.rows].sort((a, b) =>
        compareRowsBySorting(table, a, b, sorting),
      )

      return buildModel(applyPostSort(sorted) ?? sorted)
    },
    { key: 'getSortedRowModel' },
  )

  // 5b. Grouped Row Model — when `state.grouping` is non-empty, produce a
  // flattened model of synthetic group header rows (with per-column aggregates)
  // interleaved with their expanded descendant rows.
  table.getPreGroupedRowModel = table.getSortedRowModel
  table.getGroupedRowModel = memo(
    () => [table.getSortedRowModel(), table.getState().grouping, table.getState().expanded],
    (sortedModel: RowModel<TData>) =>
      buildGroupedRowModel(
        table,
        sortedModel.rows,
        table.getState().grouping ?? [],
        table.getState().expanded as Record<string, boolean>,
      ),
    { key: 'getGroupedRowModel' },
  )

  // 6. Pre-pagination = grouped (when grouping active) else sorted
  table.getPrePaginationRowModel = () =>
    (table.getState().grouping?.length ?? 0) > 0
      ? table.getGroupedRowModel()
      : table.getSortedRowModel()

  // 7. Paginated Row Model
  table.getPaginationRowModel = memo(
    () => [table.getPrePaginationRowModel(), table.getState().pagination],
    (preModel: RowModel<TData>) => {
      if (resolvedOptions.manualPagination) return preModel

      // Row virtualization renders the full (sorted/grouped) dataset directly.
      // If client pagination also sliced here, the virtualizer would only ever
      // receive one page (the default 10 rows) — making virtualization look
      // broken with no error. Bypass the slice so `enableVirtualization` works
      // over the whole dataset without a `pageSize: Infinity` workaround. See
      // issue #54. (`manualPagination` above still wins — the server controls
      // the page there.)
      if (resolvedOptions.enableVirtualization) return preModel

      const { pageIndex, pageSize } = table.getState().pagination
      // Clamp the page at read time: when filtering/data changes shrink the row
      // set below the current page, render the last valid page instead of an
      // empty out-of-range slice (the stored pageIndex is left untouched).
      const lastPage =
        pageSize > 0 ? Math.max(0, Math.ceil(preModel.rows.length / pageSize) - 1) : 0
      const safePageIndex = Math.min(Math.max(0, pageIndex), lastPage)
      const start = safePageIndex * pageSize
      const end = start + pageSize
      const paginated = preModel.rows.slice(start, end)

      const rowsById: Record<string, Row<TData>> = {}
      for (const row of paginated) {
        rowsById[row.id] = row
      }

      return { rows: paginated, flatRows: paginated, rowsById }
    },
    { key: 'getPaginationRowModel' },
  )

  // Final row model = paginated
  table.getRowModel = table.getPaginationRowModel

  // getRow
  table.getRow = (id: string, searchAll?: boolean) => {
    const model = searchAll ? table.getCoreRowModel() : table.getRowModel()
    const row = model.rowsById[id]
    if (!row) {
      throw new Error(`[yable E003] Row with id "${id}" not found.`)
    }
    return row
  }

  // Pre-expanded and pre-grouped models. Pre-grouped = the sorted model
  // (grouping runs after sorting), matching the `getGroupedRowModel` pipeline.
  table.getPreExpandedRowModel = table.getSortedRowModel
  table.getPreGroupedRowModel = table.getSortedRowModel

  // ---------------------------------------------------------------------------
  // Commit Coordinator (Task #10)
  // ---------------------------------------------------------------------------

  const setCommitsSlice = (next: CommitsSlice) => {
    table.setState((old: TableState) => ({ ...old, commits: next }))
  }

  let lastDataRef: unknown = resolvedOptions.data

  const commitCoordinator = createCommitCoordinator(
    {
      getSlice: () => table.getState().commits,
      setSlice: setCommitsSlice,
      getSavedValue: (rowId: string, columnId: string) => {
        try {
          const row = table.getRow(rowId, true)
          return row.getValue(columnId)
        } catch {
          return undefined
        }
      },
      getRow: (rowId: string) => {
        try {
          return table.getRow(rowId, true).original
        } catch {
          return undefined
        }
      },
      rowExists: (rowId: string) => {
        try {
          table.getRow(rowId, true)
          return true
        } catch {
          return false
        }
      },
    },
    {
      onCommit: resolvedOptions.onCommit as CoordinatorOptions['onCommit'],
      resolveColumnCommit: (columnId: string) => {
        const col = table.getColumn(columnId)
        type ColDefWithCommit = ColumnDef<TData> & { commit?: CoordinatorOptions['onCommit'] }
        const def = col?.columnDef as ColDefWithCommit | undefined
        return def?.commit
      },
      rowCommitRetryMode: resolvedOptions.rowCommitRetryMode ?? 'failed',
    },
  )

  commitCoordinatorMap.set(table as Table<RowData>, commitCoordinator)

  // Wire new table methods (override the literal stubs)
  table.getCellRenderValue = (rowId: string, columnId: string) => {
    const status = commitCoordinator.getCellStatus(rowId, columnId)
    if (status !== 'idle') {
      return commitCoordinator.getRenderValue(rowId, columnId)
    }
    try {
      return table.getRow(rowId, true).getValue(columnId)
    } catch {
      return undefined
    }
  }

  table.getCellStatus = (rowId: string, columnId: string) =>
    commitCoordinator.getCellStatus(rowId, columnId)

  table.getCellErrorMessage = (rowId: string, columnId: string) =>
    commitCoordinator.getRecord(rowId, columnId)?.errorMessage

  table.getCellConflictWith = (rowId: string, columnId: string) =>
    commitCoordinator.getRecord(rowId, columnId)?.conflictWith

  table.commit = async () => {
    // When autoCommit is true, the coordinator has already fired everything;
    // there's nothing buffered. When autoCommit is false, the existing
    // pendingValues from the editing slice are flushed through the coordinator.
    const editing = table.getState().editing
    const pendingValues: Record<string, Record<string, unknown>> = editing.pendingValues ?? {}
    const patches: Omit<CellPatch, 'signal' | 'row'>[] = []
    for (const rowId of Object.keys(pendingValues)) {
      for (const colId of Object.keys(pendingValues[rowId]!)) {
        let previousValue: unknown
        try {
          previousValue = table.getRow(rowId, true).getValue(colId)
        } catch {
          previousValue = undefined
        }
        patches.push({
          rowId,
          columnId: colId,
          value: pendingValues[rowId]![colId],
          previousValue,
        })
      }
    }
    if (patches.length === 0) return
    await commitCoordinator.dispatch(patches)
    table.setEditing((old: EditingState) => ({ ...old, pendingValues: {} }))
  }

  table.retryCommit = async (rowId: string, columnId: string) => {
    await commitCoordinator.retry(rowId, columnId)
  }

  table.dismissCommit = (rowId: string, columnId: string) => {
    commitCoordinator.dismiss(rowId, columnId)
  }

  table.dismissAllCommits = () => {
    commitCoordinator.dismissAll()
  }

  // Sweep triggers — fire on data ref change
  const originalSetOptions = table.setOptions
  table.setOptions = (updater: Updater<TableOptionsResolved<TData>>) => {
    originalSetOptions(updater)
    const data = table.options.data
    if (data !== lastDataRef) {
      lastDataRef = data
      commitCoordinator.runOrphanedGc()
      commitCoordinator.runAutoClearSweep()
      commitCoordinator.runConflictDetection()
    }
  }

  const resolveClipboardOptions = (options?: {
    delimiter?: string
    rowDelimiter?: string
    includeHeaders?: boolean
  }) => ({
    delimiter: options?.delimiter ?? '\t',
    rowDelimiter: options?.rowDelimiter ?? '\n',
    includeHeaders: options?.includeHeaders ?? false,
  })

  const buildCopiedCells = (
    rows: Row<TData>[],
    columns: Column<TData, unknown>[],
  ): { rowId: string; columnId: string; value: unknown }[] =>
    rows.flatMap((row) =>
      columns.map((column) => ({
        rowId: row.id,
        columnId: column.id,
        value: row.getValue(column.id),
      })),
    )

  table.copyToClipboard = (options?: {
    delimiter?: string
    rowDelimiter?: string
    includeHeaders?: boolean
  }) => {
    const resolved = resolveClipboardOptions(options)
    const rowModel = table.getRowModel()
    const visibleColumns = table.getVisibleLeafColumns()
    const range = table.getCellSelectionRange()

    let rows: Row<TData>[] = []
    let columns: Column<TData, unknown>[] = []

    if (range) {
      const normalized = normalizeCellRange(range)
      rows = rowModel.rows.slice(normalized.top, normalized.bottom + 1)
      columns = visibleColumns.slice(normalized.left, normalized.right + 1)
    } else {
      const selection = table.getState().rowSelection
      rows = rowModel.rows.filter((row: Row<TData>) => selection[row.id])
      columns = visibleColumns

      if (rows.length === 0) {
        const focusedCell = table.getFocusedCell()
        if (focusedCell) {
          const row = rowModel.rows[focusedCell.rowIndex]
          const column = visibleColumns[focusedCell.columnIndex]
          if (row && column) {
            rows = [row]
            columns = [column]
          }
        }
      }
    }

    if (rows.length === 0 || columns.length === 0) {
      return ''
    }

    const text = serializeCells(rows, columns, resolved)
    table.events.emit('clipboard:copy', {
      text,
      cells: buildCopiedCells(rows, columns),
    })
    return text
  }

  table.pasteFromClipboard = (
    text: string,
    targetRowId: string,
    targetColumnId: string,
    options?: {
      delimiter?: string
      rowDelimiter?: string
      includeHeaders?: boolean
    },
  ) => {
    const resolved = resolveClipboardOptions(options)
    const data = parseClipboardText(text, resolved)
    if (data.length === 0) return

    const pastedCells = applyCellPaste(
      table,
      data,
      targetRowId,
      targetColumnId,
      table.getRowModel().rows,
      table.getVisibleLeafColumns(),
    )

    table.events.emit('clipboard:paste', {
      text,
      cells: pastedCells,
    })
  }

  table.cutCells = (options?: {
    delimiter?: string
    rowDelimiter?: string
    includeHeaders?: boolean
  }) => {
    const text = table.copyToClipboard(options)
    if (!text) return text

    const range = table.getCellSelectionRange()
    const rowModel = table.getRowModel()
    const visibleColumns = table.getVisibleLeafColumns()
    let rows: Row<TData>[] = []
    let columns: Column<TData, unknown>[] = []

    if (range) {
      const normalized = normalizeCellRange(range)
      rows = rowModel.rows.slice(normalized.top, normalized.bottom + 1)
      columns = visibleColumns.slice(normalized.left, normalized.right + 1)
    } else {
      const focusedCell = table.getFocusedCell()
      if (focusedCell) {
        const row = rowModel.rows[focusedCell.rowIndex]
        const column = visibleColumns[focusedCell.columnIndex]
        if (row && column) {
          rows = [row]
          columns = [column]
        }
      }
    }

    const cutCells = buildCopiedCells(rows, columns)
    for (const cell of cutCells) {
      const column = table.getColumn(cell.columnId)
      if ((column?.columnDef as { editable?: boolean } | undefined)?.editable === false) {
        continue
      }
      table.setPendingValue(cell.rowId, cell.columnId, '')
    }

    table.events.emit('clipboard:cut', {
      text,
      cells: cutCells,
    })

    return text
  }

  // ---------------------------------------------------------------------------
  // Feature Engine Integrations (0.5.1)
  //
  // The methods below were declared as literal no-op stubs in the table object
  // literal so the instance satisfies the Table type. Here we override them with
  // their real engines — mirroring the commit-coordinator overrides above.
  // ---------------------------------------------------------------------------

  // Undo/redo wraps table.setPendingValue, so it MUST be installed before the
  // formula and fill engines (which write through setPendingValue) so their
  // writes are tracked. Only wired when explicitly enabled — otherwise the
  // no-op stubs remain (undo/redo stays inert).
  if (resolvedOptions.enableUndoRedo) {
    const undoRedo = createUndoRedoIntegration(table, {
      undoStackSize: resolvedOptions.undoStackSize,
    })
    table.undo = undoRedo.undo
    table.redo = undoRedo.redo
    table.canUndo = undoRedo.canUndo
    table.canRedo = undoRedo.canRedo
    table.clearUndoHistory = undoRedo.clearUndoHistory
  }

  // Full-row editing — coordinates per-cell editing through the commit
  // coordinator (single dispatch per row commit).
  const rowEditing = createFullRowEditingIntegration(table)
  table.startRowEditing = rowEditing.startRowEditing
  table.commitRowEdit = rowEditing.commitRowEdit
  table.cancelRowEdit = rowEditing.cancelRowEdit
  table.isRowEditing = rowEditing.isRowEditing
  table.getEditingRows = () => Array.from(rowEditing.getEditingRows())
  table.getEditableColumnIds = rowEditing.getEditableColumns

  // Row drag / reorder. `resolvedOptions` is mutated in place by setOptions, so
  // getData reads the live data array and setData swaps it (invalidating the
  // core row-model memo). onRowReorder is forwarded off the `row:reorder` event
  // (emitted by both `moveRow` and drag-end) so every reorder path notifies the
  // consumer exactly once.
  const rowDrag = createRowDragIntegration(table, {
    getData: () => resolvedOptions.data,
    setData: (data) => table.setOptions((prev) => ({ ...prev, data })),
  })
  table.moveRow = rowDrag.moveRow
  table.events.on('row:reorder', (event) => resolvedOptions.onRowReorder?.(event))

  // Pivot row model accessor. The returned rows carry their aggregates on
  // `.original`; framework adapters can register generated pivot column defs
  // when they need to render the row model directly.
  table.getPivotRowModel = () => {
    const stateConfig = table.getState().pivot.config
    const config = stateConfig.valueFields.length > 0 ? stateConfig : resolvedOptions.pivotConfig

    return getPivotRowModel(
      table,
      resolvedOptions.data,
      // The public PivotConfig widens `aggregation` to `string`; the engine
      // narrows to known fn names and treats unknown names as no-op (null).
      (config ?? {
        rowFields: [],
        columnFields: [],
        valueFields: [],
      }) as PivotEngineConfig,
    ).rowModel
  }

  // Fill handle — detect a pattern from the source range and fill the target.
  table.fillRange = (sourceRange, targetRange) => {
    detectAndFill(
      table,
      sourceRange,
      targetRange,
      table.getRowModel().rows,
      table.getVisibleLeafColumns(),
    )
  }

  // Formula engine. Bound AFTER undo/redo wraps setPendingValue so computed
  // writes are undoable. cell.getValue() surfaces the computed value for any
  // raw cell whose value starts with '='.
  const formulaEngine = new FormulaEngine(table)
  table.setFormula = (rowId, columnId, formula) =>
    formulaEngine.setFormula(rowId, columnId, formula)
  table.getFormula = (rowId, columnId) => formulaEngine.getFormula(rowId, columnId)
  table.evaluateFormulas = () => formulaEngine.evaluateAll()

  return table
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defaultCompare(a: unknown, b: unknown): number {
  if (a === b) return 0
  if (a == null) return -1
  if (b == null) return 1

  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1

  return String(a).localeCompare(String(b))
}

function rowPassesFilters<TData extends RowData>(
  table: Table<TData>,
  row: Row<TData>,
  columnFilters: ColumnFiltersState,
  globalFilter: unknown,
): boolean {
  return (
    rowPassesColumnFilters(table, row, columnFilters) &&
    rowPassesGlobalFilter(table, row, globalFilter)
  )
}

function rowPassesColumnFilters<TData extends RowData>(
  table: Table<TData>,
  row: Row<TData>,
  columnFilters: ColumnFiltersState,
): boolean {
  for (const filter of columnFilters) {
    // T1-05: Skip invalid column IDs silently
    const column = table.getColumn(filter.id)
    if (!column) continue

    const filterFn = column.getFilterFn()

    if (filterFn) {
      // T1-09: Wrap user-provided filterFn in try-catch
      try {
        if (!filterFn(row, filter.id, filter.value, () => {})) {
          return false
        }
        continue
      } catch (err) {
        console.error(
          `[yable E010] filterFn threw for column "${filter.id}", row "${row.id}":`,
          err,
        )
        continue // safe default: include the row for this filter
      }
    }

    const value = row.getValue(filter.id)
    if (Array.isArray(filter.value)) {
      if (!filter.value.some((candidate: unknown) => value === candidate)) {
        return false
      }
      continue
    }

    // Default string include filter
    if (
      !String(value ?? '')
        .toLowerCase()
        .includes(String(filter.value ?? '').toLowerCase())
    ) {
      return false
    }
  }

  return true
}

function rowPassesGlobalFilter<TData extends RowData>(
  table: Table<TData>,
  row: Row<TData>,
  globalFilter: unknown,
): boolean {
  if (!globalFilter) return true

  const searchStr = String(globalFilter).toLowerCase()
  return table.getAllLeafColumns().some((col: Column<TData, unknown>) => {
    const value = row.getValue(col.id)
    return String(value ?? '')
      .toLowerCase()
      .includes(searchStr)
  })
}

function createTreeNodeRow<TData extends RowData>(
  table: Table<TData>,
  node: TreeNode<TData>,
): Row<TData> {
  return createRow(table, node.key, node.data, 0, node.depth, [], undefined)
}

function createTreeNodeComparator<TData extends RowData>(
  table: Table<TData>,
  sorting: SortingState,
): (a: TreeNode<TData>, b: TreeNode<TData>) => number {
  const rows = new Map<string, Row<TData>>()
  const getRow = (node: TreeNode<TData>) => {
    let row = rows.get(node.key)
    if (!row) {
      row = createTreeNodeRow(table, node)
      rows.set(node.key, row)
    }
    return row
  }

  return (a, b) => compareRowsBySorting(table, getRow(a), getRow(b), sorting)
}

function compareRowsBySorting<TData extends RowData>(
  table: Table<TData>,
  a: Row<TData>,
  b: Row<TData>,
  sorting: SortingState,
): number {
  for (const sort of sorting) {
    // T1-05: Skip invalid column IDs silently
    const column = table.getColumn(sort.id)
    if (!column) continue

    // Custom sort function
    const sortFn = column.getSortingFn()
    if (sortFn) {
      // T1-09: Wrap user-provided sortFn in try-catch
      try {
        const result = sortFn(a, b, sort.id)
        if (result !== 0) return sort.desc ? -result : result
        continue
      } catch (err) {
        console.error(`[yable E011] sortingFn threw for column "${sort.id}":`, err)
        continue // safe default: treat as equal
      }
    }

    // Default comparison
    const aVal = a.getValue(sort.id)
    const bVal = b.getValue(sort.id)
    const result = defaultCompare(aVal, bVal)
    if (result !== 0) return sort.desc ? -result : result
  }
  return 0
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}
